import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, NursingInterventionsViewFragmentAliases,  } from '../../../../codegen';
import BaseController from '../../../../controllers/BaseController';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import { NursingInterventionsTypes } from './nursing-interventions-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../constants/date-utils';
import { AdministrationType, DurationType } from '../cpoe-view-types';

@Controller({ fragment: FragmentNames.NURSING_INTERVENTIONS_VIEW_FRAGMENT })
class NursingInterventionsViewController extends BaseController {

  @Inject(NursingInterventionsViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NursingInterventionsViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(NursingInterventionsViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NursingInterventionsViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NursingInterventionsViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @OnMount(NursingInterventionsViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nursingInterventionsId, patientId  } = this.historyService.getCurrentRouteParams()
    this.fetchNursingInterventionsDetails(String(nursingInterventionsId), String(patientId))
  }

  @OnMount(NursingInterventionsViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const nursingInterventionsView = this.context.get('nursingInterventionsView') as NursingInterventionsTypes
    if(nursingInterventionsView) {
      const { interval, interventions, side, topography } = nursingInterventionsView;
      this.formMainInformationsAPI.setValue({
        interval,
        interventions,
        side,
        topography,
      })
    }
  }

  @OnMount(NursingInterventionsViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const nursingInterventionsView = this.context.get('nursingInterventionsView') as NursingInterventionsTypes

    if(nursingInterventionsView) {
      const {administration, duration, hours, now, durationType, administrationType} = nursingInterventionsView
      const endDate = moment(String(nursingInterventionsView.endDate)).utc().format(ISO_DATE_INPUT)
      const startDate = moment(String(nursingInterventionsView.startDate)).utc().format(ISO_DATE_INPUT)

      this.formPeriodValidityAPI.setValue({
        administration,
        duration,
        endDate,
        hours,
        now,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
      this.inputEndDateAPI.setValue(endDate)
      this.showFieldsByDurationType(durationType);
      this.showFieldsByAdministrationType(administrationType);
    }
  }

  @OnMount(NursingInterventionsViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const nursingInterventionsView = this.context.get('nursingInterventionsView') as NursingInterventionsTypes
    if(nursingInterventionsView) {
      const { observation, prescriberDt, prescriberName } = nursingInterventionsView

      this.formAddicionalInformationsAPI.setValue({
        observation,
        prescriberDt,
        prescriberName
      })
      this.showFieldObservation(observation)
    }
  }

  private fetchNursingInterventionsDetails(nursingInterventionsId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_NURSING_INTERVENTIONS_DETAILS, { nursingInterventionsId, patientId })
      .then((response: HttpResponse) => {
        const nursingInterventionsView = response.body as NursingInterventionsTypes;
        this.context.set({ nursingInterventionsView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showFieldObservation(observation: string): void {
    this.fieldObservationAPI.setVisible(!!observation)
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME)
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nursing-interventions',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default NursingInterventionsViewController;